import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import LogoTray from './LogoTray';
import { ArticleGrid, ArticleImage, ArticleViewPicker, useArticleView } from './ArticleView';
import { notificationApi } from '../utils/newsNotifications';
import '../Search.css';

export default function NotificationFeed() {
  const { subscriptionId, batchToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [feed, setFeed] = useState(null);
  const [articleView, setArticleView] = useArticleView();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadedKey, setLoadedKey] = useState('');
  const [needsLogin, setNeedsLogin] = useState(false);
  const feedReady = Boolean(feed);
  const path = `/${encodeURIComponent(subscriptionId)}/batches/${encodeURIComponent(batchToken)}`;
  useEffect(() => {
    let cancelled = false;
    setFeed(null);
    setLoadedKey('');
    setError('');
    setNeedsLogin(false);
    const token = localStorage.getItem('token');
    if (!token) return;
    setBusy(true);
    notificationApi(`${path}/open`, 'POST', { offset: 0, limit: 30 }).then(data => {
      if (!cancelled && localStorage.getItem('token') === token) { setFeed(data); setLoadedKey(path); }
    }).catch(err => { if (!cancelled) { setNeedsLogin(err.status === 401); setError(err.status === 404 ? 'Ova obavijest više nije dostupna.' : err.message); } })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [path]);
  // Delivery acknowledgement does not mark news read. Only a rendered feed
  // does; failures to load or authenticate leave all batches unopened.
  useEffect(() => {
    if (!feedReady || loadedKey !== path) return;
    let cancelled = false;
    let retry;
    const markRead = async () => {
      try { await notificationApi(`${path}/read`, 'POST'); }
      catch (err) { if (!cancelled && ![401, 404].includes(err.status)) retry = setTimeout(markRead, 15000); }
    };
    markRead();
    return () => { cancelled = true; clearTimeout(retry); };
  }, [loadedKey, path, feedReady]);

  const loadMore = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await notificationApi(`${path}/open`, 'POST', { offset: feed.articles.length, limit: 30 });
      setFeed(previous => ({ ...data, articles: [...previous.articles, ...data.articles] }));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const loggedIn = Boolean(localStorage.getItem('token')) && !needsLogin;
  return <div className="notification-feed-page">
    <LogoTray />
    <main className="notification-feed-content">
      <div className="notification-feed-heading"><h1>{feed ? `Nove vijesti · ${feed.name}` : 'Nove vijesti'}</h1>
        <button type="button" onClick={() => navigate('/')}>Početna</button></div>
      {!loggedIn ? <div><p>Prijavite se da otvorite svoje obavijesti.</p><button type="button" onClick={() => navigate('/login', { state: { returnTo: location.pathname } })}>Prijavi se</button></div> : <>
        {feed && <div className="notification-feed-meta">{feed.total} vijesti iz ove i prethodnih neotvorenih obavijesti</div>}
        {feed && <div className="results-toolbar"><h2>Vijesti</h2><ArticleViewPicker view={articleView} onChange={setArticleView} /></div>}
        {feed && <ArticleGrid view={articleView}>{feed.articles.map(article => <a key={article.id} className={`article-card${article.image?.startsWith('/static/') ? '' : ' no-image'}`} href={article.link} target="_blank" rel="noopener noreferrer">
          {article.image?.startsWith('/static/') && <ArticleImage src={`${process.env.REACT_APP_API_URL || '/api'}${article.image}`} />}
          <div className="article-body">
            <div className="article-meta">
              <span className="article-source">{article.portal_name}</span>
              <span className="article-meta-dot"></span>
              <span className="article-date">{article.publish_date}</span>
              {(feed.show_subcategory_on_articles ?? true) && article.tag && <>
                <span className="article-meta-dot"></span>
                <span className="article-subcategory">{article.tag}</span>
              </>}
            </div>
            <h3 className="article-title">{article.title}</h3>
          </div>
        </a>)}</ArticleGrid>}
        {feed && !feed.articles.length && <p>Nema dostupnih vijesti u ovoj obavijesti.</p>}
        {busy && <p role="status">Učitavanje…</p>}
        {feed?.has_more && <button type="button" disabled={busy} onClick={loadMore}>Učitaj još</button>}
        {error && <p role="alert">{error}</p>}
      </>}
    </main>
  </div>;
}
